import type React from 'react';
import { useState } from 'react';
import {
  FormControl,
  FormGroup,
  InputCheckBox,
  InputDateTimev2,
  InputSelect
} from '@itandi/itandi-bb-ui';
import type { UseFormReturn } from 'react-hook-form';
import { useFormContext, FormProvider } from 'react-hook-form';
import { isOriginVendor } from '~/models/DealVendorCompany';
import { InspectionCandidateDatesList } from '../../inspection_candidate_dates/InspectionCandidateDatesList';
import { useVendorUsers } from '~/hooks/vendors/useVendorUsers';
import type { Deal } from '~/models/Deal';
import type { VisitScheduleInputVendorParam } from '~/models/DealVisitDate';
import type { VendorSession } from '~/models/VendorSession';

type Props = {
  deal: Deal;
  session: VendorSession;
};

type ScheduleFormProps = Props & {
  methods: UseFormReturn<VisitScheduleInputVendorParam>;
};

/**
 * 立会日程・現地調査日程登録フォーム（内部コンポーネント）
 * @for vendor
 */

const InnerScheduleForm: React.FC<Props> = ({ deal, session }) => {
  const { data: vendorUsers } = useVendorUsers().index();
  const { register, setValue, watch } =
    useFormContext<VisitScheduleInputVendorParam>();
  const inspectionDate = watch('inspectionDate');
  const surveyDate = watch('surveyDate');
  const inspectionByVendor = deal.inspectionBy === 'vendor';

  const [syncInspectionDate, setSyncInspectionDate] = useState(true);
  return (
    <>
      <InspectionCandidateDatesList deal={deal} />
      <FormGroup variant="Vertical">
        {inspectionByVendor && (
          <FormControl label="立会実施日程">
            <InputDateTimev2
              onChange={(date): void => {
                setValue('inspectionDate', date);
                if (syncInspectionDate) {
                  setValue('surveyDate', date);
                }
              }}
              value={inspectionDate}
            />
          </FormControl>
        )}
        <FormControl label="現地調査実施日程">
          {inspectionByVendor && (
            <InputCheckBox
              checked={syncInspectionDate}
              label="立会実施日程と同じ値で補完"
              onChange={(e): void => {
                setSyncInspectionDate(e.target.checked);
                if (e.target.checked) {
                  setValue('surveyDate', inspectionDate);
                }
              }}
            />
          )}
          <InputDateTimev2
            disabled={inspectionByVendor && syncInspectionDate}
            marginTop={8}
            onChange={(date): void => {
              setValue('surveyDate', date);
            }}
            value={surveyDate}
          />
        </FormControl>
        {!session.vendorCompany.usePartner &&
          !isOriginVendor(deal, session.vendorCompany.id) &&
          vendorUsers !== null && (
            <FormControl label="施工会社担当者">
              <InputSelect width="100%" {...register('vendorUserId')}>
                <option value="">選択してください</option>
                {vendorUsers.map((vendorUser) => (
                  <option key={vendorUser.id} value={vendorUser.id}>
                    {vendorUser.name}
                  </option>
                ))}
              </InputSelect>
            </FormControl>
          )}
      </FormGroup>
    </>
  );
};

/**
 * 立会日程・現地調査日程登録フォーム
 * @for vendor
 */

export const ScheduleForm: React.FC<ScheduleFormProps> = ({
  methods,
  deal,
  session
}) => (
  <FormProvider {...methods}>
    <InnerScheduleForm deal={deal} session={session} />
  </FormProvider>
);
